import { NavLink } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { PROJECT_BROWSE_VIEWS } from '@modules-rocco/projects/config/project-browse-views';
import type { ProjectBrowseView } from '@modules-rocco/projects/config/project-browse-views';
import { DARK_SELECTED, DARK_SELECTED_HOVER } from '~/ui-rocco/button-system';
import { SCROLL_TO_PILL_BAR_STATE } from './use-scroll-to-pill-bar';

/**
 * Projects / Spaces / Styles, as pills in the filter row.
 *
 * They used to be a tab strip of their own above the listing — a title, a
 * sub-label naming the axis, and a rule under the current one. That is a lot of
 * furniture for three links, and it sat in the band where the page states what
 * it is. As pills they read as what they are: a switch between three ways into
 * the same index, beside the categories that refine it.
 *
 * Styled as peers of the category pills rather than a set of their own, so the
 * row reads as one control. The divider is what separates the two jobs.
 */
export const ProjectViewPills = ({
    activeView,
    isDark,
}: {
    activeView: ProjectBrowseView;
    isDark: boolean;
}) => (
    <ul className="m-0 flex shrink-0 list-none items-center gap-2 p-0">
        {PROJECT_BROWSE_VIEWS.map(({ view, path, label }) => {
            const isActive = view === activeView;
            return (
                <li key={view}>
                    <NavLink
                        to={path}
                        // Switching view replaces the listing, so the reader
                        // lands with this row at the top and the new grid
                        // under it rather than above the fold of a page they
                        // have already read.
                        state={SCROLL_TO_PILL_BAR_STATE}
                        // `/projects` is a prefix of `/projects/spaces`, so
                        // without this NavLink marks Projects current on every
                        // one of the three views.
                        end
                        // The set is a switch, so the current one is worth
                        // announcing rather than leaving to colour alone.
                        aria-current={isActive ? 'page' : undefined}
                        className={cn(
                            'flex h-[2.75rem] shrink-0 items-center justify-center rounded-[1rem] px-[1.3125rem] text-base whitespace-nowrap transition-colors',
                            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                            isDark
                                ? isActive
                                    ? cn(DARK_SELECTED, DARK_SELECTED_HOVER)
                                    : 'bg-[#141414] text-offwhite hover:bg-[#3d3d3d]'
                                : isActive
                                  ? 'bg-[#323232] text-white hover:bg-[#484848]'
                                  : 'bg-white text-[#323232] hover:bg-[#f2f2f2]',
                        )}
                    >
                        {label}
                    </NavLink>
                </li>
            );
        })}
    </ul>
);
